import { useForm } from 'react-hook-form'

import { yupResolver } from '@hookform/resolvers/yup'
import { DynamicFormProps } from '@ui/interfaces'
import { SaveAndCancelButtons } from '@ui/pages/pipelinesWorkflow/components/DestinationForms/SaveAndCancelButtons'
import * as Yup from 'yup'

const regions = [
  ['us-east-2', 'US East (Ohio) | us-east-2'],
  ['us-east-1', 'US East (Virginia) | us-east-1'],
  ['us-west-1', 'US West (N. California) | us-west-1'],
  ['us-west-2', 'US West (Oregon) | us-west-2'],
  ['af-south-1', 'Africa (Cape Town) | af-south-1'],
  ['ap-east-1', 'Asia Pacific (Hong Kong) | ap-east-1'],
  ['ap-south-2', 'Asia Pacific (Hyderabad) | ap-south-2'],
  ['ap-southeast-3', 'Asia Pacific (Jakarta) | ap-southeast-3'],
  ['ap-southeast-4', 'Asia Pacific (Melbourne) | ap-southeast-4'],
  ['ap-south-1', 'Asia Pacific (Mumbai) | ap-south-1'],
  ['ap-northeast-3', 'Asia Pacific (Osaka) | ap-northeast-3'],
  ['ap-northeast-2', 'Asia Pacific (Seoul) | ap-northeast-2'],
  ['ap-southeast-1', 'Asia Pacific (Singapore) | ap-southeast-1'],
  ['ap-southeast-2', 'Asia Pacific (Sydney) | ap-southeast-2'],
  ['ap-northeast-1', 'Asia Pacific (Tokyo) | ap-northeast-1'],
  ['ca-central-1', 'Canada (Central) | ca-central-1'],
  ['ca-west-1', 'Canada West (Calgary) | ca-west-1'],
  ['eu-central-1', 'Europe (Frankfurt) | eu-central-1'],
  ['eu-west-1', 'Europe (Ireland) | eu-west-1'],
  ['eu-west-2', 'Europe (London) | eu-west-2'],
  ['eu-south-1', 'Europe (Milan) | eu-south-1'],
  ['eu-west-3', 'Europe (Paris) | eu-west-3'],
  ['eu-south-2', 'Europe (Spain) | eu-south-2'],
  ['eu-north-1', 'Europe (Stockholm) | eu-north-1'],
  ['eu-central-2', 'Europe (Zurich) | eu-central-2'],
  ['il-central-1', 'Israel (Tel Aviv) | il-central-1'],
  ['me-south-1', 'Middle East (Bahrain) | me-south-1'],
  ['me-central-1', 'Middle East (UAE) | me-central-1'],
  ['sa-east-1', 'South America (São Paulo) | sa-east-1'],
]

export default function AwsS3(props: DynamicFormProps) {
  const schema = Yup.object({
    aws_region: Yup.string()
      .required('AWS Region is required')
      .oneOf(regions.map((r) => r[0])),
    aws_access_key_id: Yup.string().required('AWS Access Key ID is required'),
    aws_secret_access_key: Yup.string().required('AWS Secret Access Key is required'),
    aws_bucket: Yup.string().required('AWS Bucket is required'),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...props.config,
    },
  })

  const { saveClick, cancelClick } = props

  return (
    <form onSubmit={handleSubmit(saveClick)}>
      {props.additionalInputs}
      <div className="grid grid-cols-1 gap-x-6">
        <label htmlFor="aws_region" className="block text-sm font-medium leading-6 text-gray-900">
          AWS Region*
        </label>
        <div className="mt-2">
          <select
            {...register('aws_region')}
            id="aws_region"
            name="aws_region"
            className="w-50 block rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            disabled={props.disableInputs}>
            {regions.map((region) => (
              <option key={region[0]} value={region[0]}>
                {region[1]}
              </option>
            ))}
          </select>

          {errors.aws_region && <p className="mt-2 text-xs italic text-red-500">{errors.aws_region?.message}</p>}
        </div>

        <label htmlFor="aws_access_key_id" className="block text-sm font-medium leading-6 text-gray-900">
          AWS Access Key ID
        </label>
        <div className="mt-2">
          <input
            {...register('aws_access_key_id')}
            type="text"
            id="aws_access_key_id"
            name="aws_access_key_id"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            placeholder="abc123"
            disabled={props.disableInputs}
          />
          {errors['aws_access_key_id'] && (
            <p className="mt-2 text-xs italic text-red-500">{errors['aws_access_key_id'].message}</p>
          )}
        </div>

        <label htmlFor="aws_secret_access_key" className="block text-sm font-medium leading-6 text-gray-900">
          AWS Secret Access Key
        </label>
        <div className="mt-2">
          <input
            {...register('aws_secret_access_key')}
            type="text"
            name="aws_secret_access_key"
            id="aws_secret_access_key"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            placeholder="abc123"
            disabled={props.disableInputs}
          />
          {errors['aws_secret_access_key'] && (
            <p className="mt-2 text-xs italic text-red-500">{errors['aws_secret_access_key'].message}</p>
          )}
        </div>

        <label htmlFor="aws_bucket" className="block text-sm font-medium leading-6 text-gray-900">
          AWS Bucket
        </label>
        <div className="mt-2 w-full">
          <div className="flex w-full rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600">
            <span className="flex select-none items-center rounded-l-md border-r bg-gray-300/20 pl-3 pr-2 text-gray-500 sm:text-sm">
              s3://
            </span>
            <input
              {...register('aws_bucket')}
              type="text"
              name="aws_bucket"
              id="aws_bucket"
              className="block w-full flex-1 border-0 bg-transparent py-1.5 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
              placeholder="my-aws-bucket"
              disabled={props.disableInputs}
            />
          </div>
          {errors['aws_bucket'] && <p className="mt-2 text-xs italic text-red-500">{errors['aws_bucket'].message}</p>}
        </div>
      </div>
      {props.submitButton ? props.submitButton : <SaveAndCancelButtons cancelClick={cancelClick} />}
    </form>
  )
}
